import React, { useState, useEffect, FC } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Button from "../../components/ui/Button";
import JobCard from "../../components/ui/JobCard";
import { UserProfileData, RecentJob } from "../../types/components";
import { LocalStorageUser } from "../../types/localStorage";
import StatsCard from "../../components/ui/StatsCard";
import { useJobSeeker } from "../../context/JobSeekerContext";
import jobService from "../../services/job.service";

const UserDashboard: FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { isProfileComplete, isOnboardingPaymentDone } = useJobSeeker();
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const [justRegistered, setJustRegistered] = useState<boolean>(false);

  const [stats, setStats] = useState({
    applications: 0,
    interviews: 0,
    offers: 0,
    profileViews: 0,
  });
  useEffect(() => {
    // Load profile data from localStorage for testing
    if (user) {
      const savedData = localStorage.getItem(
        `profileData_${user.role}_${user.userId}`
      );
      if (savedData) {
        try {
          setProfileData(JSON.parse(savedData));
        } catch (error) {
          console.error("Error parsing profile data:", error);
        }
      }
      if (!isProfileComplete) {
        navigate(`/user/profile/complete?step=${isOnboardingPaymentDone ? 1 : 0}`, {
          replace: true,
        });
      }
      setJustRegistered(isProfileComplete || false);
    }
  }, [user, isProfileComplete]);

  useEffect(() => {
    if (user) {
      jobService.getUserApplications(user.userId, 1, 10).then((response) => {
        setStats((prevStats) => ({
          ...prevStats,
          applications: response.data.totalCount || 0,
        }));
      });
    }
  }, [user]);

  // Stats removed in JSX version

  const recentJobs: RecentJob[] = [
    // {
    //   id: 1,
    //   title: 'Senior React Developer',
    //   company: 'TechCorp',
    //   location: 'San Francisco, CA',
    //   type: 'Full-time',
    //   salary: { min: 120000, max: 150000 },
    //   postedDate: '2024-01-15',
    //   description: 'We are looking for an experienced React developer to join our team...',
    //   skills: ['React', 'TypeScript', 'Node.js', 'AWS'],
    //   isRemote: true,
    //   isUrgent: false,
    //   experience: '5+ years',
    //   matchScore: 95
    // },
    // {
    //   id: 2,
    //   title: 'Frontend Engineer',
    //   company: 'StartupXYZ',
    //   location: 'New York, NY',
    //   type: 'Full-time',
    //   salary: { min: 100000, max: 130000 },
    //   postedDate: '2024-01-14',
    //   description: 'Join our growing team as a Frontend Engineer...',
    //   skills: ['Vue.js', 'JavaScript', 'CSS', 'HTML'],
    //   isRemote: false,
    //   isUrgent: true,
    //   experience: '3+ years',
    //   matchScore: 87
    // },
    // {
    //   id: 3,
    //   title: 'Full Stack Developer',
    //   company: 'InnovateLab',
    //   location: 'Austin, TX',
    //   type: 'Contract',
    //   salary: { min: 80, max: 120 },
    //   postedDate: '2024-01-13',
    //   description: 'Looking for a versatile full-stack developer...',
    //   skills: ['React', 'Node.js', 'MongoDB', 'Docker'],
    //   isRemote: true,
    //   isUrgent: false,
    //   experience: '4+ years',
    //   matchScore: 92
    // }
  ];

  const profileCompletion = 75; // kept for potential future use

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen rounded-2xl p-4 md:p-8 text-primary-black">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-xl md:text-3xl font-bold mb-1 md:mb-2">
              Welcome back, {user?.firstName || "User"}
            </h1>
            <p className="text-sm md:text-lg opacity-90">
              Ready to find your next opportunity? Let's get started!
            </p>
          </div>
        </div>
      </div>

      {/* Profile Completion removed in JSX version */}

      {user?.role === "Intern" && (
        <div
          className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4"
          role="alert"
        >
          <p className="font-bold">Upgrade to Jobseeker</p>
          <p>
            As an Intern, you have limited access to job opportunities. Upgrade
            to a Jobseeker account to unlock all features.
          </p>
          <Button onClick={() => navigate("/user/payment")} className="mt-2">
            Upgrade Now
          </Button>
        </div>
      )}

      {/* Expert Message - Show when user just registered OR profile data is submitted */}
      {justRegistered || profileData ? (
        <div className="bg-gradient-to-r from-primary-lightGreen/10 to-primary-teaGreen/10 border border-primary-lightGreen/20 rounded-2xl p-6 md:p-8 shadow-soft">
          <div className="text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-primary-lightGreen/20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              <svg
                className="w-8 h-8 md:w-10 md:h-10 text-primary-lightGreen"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
              Please wait, our expert is finding the best job for you
            </h2>
            <p className="text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto">
              We're analyzing your profile and matching you with the most
              suitable opportunities. You can track your applications in the
              meantime.
            </p>
            {/* Removed View Applications CTA per request */}
          </div>
        </div>
      ) : (
        /* Quick Actions - Show when profile data is not submitted */
        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-soft">
          <h2 className="text-lg md:text-xl font-semibold text-primary-black mb-4 md:mb-6">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            <Link to="/user/apply">
              <Button
                className="w-full justify-start text-xs md:text-sm"
                variant="outline"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Apply for Jobs
              </Button>
            </Link>
            <Link to="/user/status">
              <Button
                className="w-full justify-start text-xs md:text-sm"
                variant="outline"
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                Application Status
              </Button>
            </Link>
          </div>
        </div>
      )}
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-1 sm:gap-2 lg:gap-3 -mb-1">
        <StatsCard
          title="Applications Sent"
          value={stats.applications}
          change="0"
          changeType="positive"
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          }
          color="primary"
        />
        <StatsCard
          title="Interviews"
          value={stats.interviews}
          change="0"
          changeType="positive"
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          }
          color="blue"
        />
        <StatsCard
          title="Job Offers"
          value={stats.offers}
          change="0"
          changeType="positive"
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          color="green"
        />
        <StatsCard
          title="Profile Views"
          value={stats.profileViews}
          change="0"
          changeType="positive"
          icon={
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
          }
          color="purple"
        />
      </div>
      {/* Recommended Jobs */}
      {recentJobs.length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-primary-black">
              Recommended Jobs
            </h2>
          </div>
          <div className="space-y-4">
            {recentJobs.map((job) => (
              <JobCard key={job.id} job={job} showMatchScore />
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {/* <div className="bg-white rounded-2xl p-6 shadow-soft">
        <h2 className="text-xl font-semibold text-primary-black mb-6">Recent Activity</h2>
        <div className="space-y-4">
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-primary-lightGreen rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-black">Application submitted</p>
              <p className="text-sm text-gray-500">Senior React Developer at TechCorp</p>
            </div>
            <span className="text-xs text-gray-400">2 hours ago</span>
          </div>
          
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-primary-teaGreen rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-black">Profile viewed</p>
              <p className="text-sm text-gray-500">By 3 companies this week</p>
            </div>
            <span className="text-xs text-gray-400">1 day ago</span>
          </div>
          
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-primary-lightGreen/20 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-lightGreen" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-black">Interview scheduled</p>
              <p className="text-sm text-gray-500">Frontend Engineer at StartupXYZ</p>
            </div>
            <span className="text-xs text-gray-400">3 days ago</span>
          </div>
        </div>
      </div> */}

      {/* Profile Data Display - For Testing */}
      {profileData && (
        <div className="bg-white rounded-2xl p-6 shadow-soft">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-primary-black">
              📋 Submitted Profile Data (Testing)
            </h2>
            <div className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
              Form Completed
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Information */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-800 border-b pb-2">
                Personal Information
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Name:</span>{" "}
                  {profileData.firstName} {profileData.lastName}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {profileData.email}
                </div>
                <div>
                  <span className="font-medium">Phone:</span> {profileData.phone}
                </div>
                {profileData.dateOfBirth && (
                  <div>
                    <span className="font-medium">DOB:</span>{" "}
                    {profileData.dateOfBirth}
                  </div>
                )}
                {profileData.gender && (
                  <div>
                    <span className="font-medium">Gender:</span>{" "}
                    {profileData.gender}
                  </div>
                )}
                {profileData.address && (
                  <div>
                    <span className="font-medium">Address:</span>{" "}
                    {profileData.address}
                  </div>
                )}
              </div>
            </div>

            {/* Professional Details */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-800 border-b pb-2">
                Professional Details
              </h3>
              <div className="space-y-2 text-sm">
                {profileData.headline && (
                  <div>
                    <span className="font-medium">Headline:</span>{" "}
                    {profileData.headline}
                  </div>
                )}
                {profileData.summary && (
                  <div>
                    <span className="font-medium">Summary:</span>
                    <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      {profileData.summary}
                    </div>
                  </div>
                )}
                {profileData.skills && profileData.skills.length > 0 && (
                  <div>
                    <span className="font-medium">Skills:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {profileData.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-primary-lightGreen/20 text-primary-lightGreen text-xs rounded"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Education & Preferences */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-800 border-b pb-2">
                Education & Preferences
              </h3>
              <div className="space-y-2 text-sm">
                {profileData.education && (
                  <div>
                    <span className="font-medium">Education:</span>
                    <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      {profileData.education}
                    </div>
                  </div>
                )}
                {profileData.jobTypes && profileData.jobTypes.length > 0 && (
                  <div>
                    <span className="font-medium">Job Types:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {profileData.jobTypes.map((type, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {profileData.workModes && profileData.workModes.length > 0 && (
                  <div>
                    <span className="font-medium">Work Modes:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {profileData.workModes.map((mode, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded"
                        >
                          {mode}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {profileData.resume && (
                  <div>
                    <span className="font-medium">Resume:</span>{" "}
                    {profileData.resume.name || "Uploaded"}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
